import getCDNIconUrl from './getCDNIconUrl';
import getFileSpriteIconClass from './getFileSpriteIconClass';
import { getExtensionFromFileName } from 'owa-file';

/**
 * Important Notes:
 * When adding a new FileIcon, we need to add extraSmall, small and medium icons into sprite icons.
 * Developers need to change Chicago code to upload cdn icons into CDN or pass true as useGenericCDNIcon.
 */

export interface FileIcon {
    extraSmall: string; // The sprite icon css for extra small size
    small: string; // The sprite icon css for small size
    medium: string; // The sprite icon css for medium size
    large: string; // The sprite icon css for large size
    cdn: string; // The cnd icon url
}

export function getFileIconFromSprite(iconName: string, useGenericCDNIcon?: boolean): FileIcon {
    return {
        extraSmall: getFileSpriteIconClass(`${iconName}_16x16`),
        small: getFileSpriteIconClass(`${iconName}_20x20`),
        medium: getFileSpriteIconClass(`${iconName}_32x32`),
        large: getFileSpriteIconClass(`${iconName}_64x64`),
        cdn: useGenericCDNIcon
            ? getCDNIconUrl('generic_16x16')
            : getCDNIconUrl(`${iconName}_16x16`),
    };
}

const folderIcon = {
    extraSmall: null, // There is no extra small icon available for folder
    small: getFileSpriteIconClass('folder_20x20'),
    medium: getFileSpriteIconClass('folder_32x32'),
    large: getFileSpriteIconClass('folder_64x64'),
    cdn: getCDNIconUrl('folder_16x16'),
};

const accessFileIcon = getFileIconFromSprite('accdb');
const audioFileIcon = getFileIconFromSprite('audio');
const calendarFileIcon = getFileIconFromSprite('calendar');
const codeFileIcon = getFileIconFromSprite('code');
const csvFileIcon = getFileIconFromSprite('csv');
const excelFileIcon = getFileIconFromSprite('xlsx');
const excelTemplateFileIcon = getFileIconFromSprite('xltx');
const exeFileIcon = getFileIconFromSprite('exe');
const genericFileIcon = getFileIconFromSprite('generic');
const gdocFileIcon = getFileIconFromSprite('gdoc', true);
const gformFileIcon = getFileIconFromSprite('gform', true);
const gsheetFileIcon = getFileIconFromSprite('gsheet', true);
const gslideFileIcon = getFileIconFromSprite('gslide', true);
const infopathFileIcon = getFileIconFromSprite('infopath');
const linkFileIcon = getFileIconFromSprite('link');
const msgFileIcon = getFileIconFromSprite('email');
const oneFileIcon = getFileIconFromSprite('one');
const onepkgFileIcon = getFileIconFromSprite('onetoc');
const pblshFileIcon = getFileIconFromSprite('pblsh');
const pdfFileIcon = getFileIconFromSprite('pdf');
const photoFileIcon = getFileIconFromSprite('photo');
const powerpointFileIcon = getFileIconFromSprite('pptx');
const powerpointTemplateFileIcon = getFileIconFromSprite('potx');
const powerpointSlideShowFileIcon = getFileIconFromSprite('ppsx');
const projectFileIcon = getFileIconFromSprite('project');
const projectTemplateFileIcon = getFileIconFromSprite('mpt');
const rpmsgFileIcon = getFileIconFromSprite('rpmsg');
const txtFileIcon = getFileIconFromSprite('txt');
const vectorFileIcon = getFileIconFromSprite('vector');
const videoFileIcon = getFileIconFromSprite('video');
const visioDrawingFileIcon = getFileIconFromSprite('vsdx');
const visioStencilFileIcon = getFileIconFromSprite('vssx');
const visioTemplateFileIcon = getFileIconFromSprite('vstx');
const wordFileIcon = getFileIconFromSprite('docx');
const wordTemplateFileIcon = getFileIconFromSprite('dotx');
const zipFileIcon = getFileIconFromSprite('zip');

const wellKnownFileIconMap: { [key: string]: FileIcon } = Object.freeze({
    '.3g2': videoFileIcon,
    '.3gp': videoFileIcon,
    '.aac': audioFileIcon,
    '.accdb': accessFileIcon,
    '.accdc': accessFileIcon,
    '.accde': accessFileIcon,
    '.accdr': accessFileIcon,
    '.accdt': accessFileIcon,
    '.acs': accessFileIcon,
    '.ai': vectorFileIcon,
    '.aiff': audioFileIcon,
    '.ape': audioFileIcon,
    '.asf': videoFileIcon,
    '.aspx': codeFileIcon,
    '.asx': videoFileIcon,
    '.avi': videoFileIcon,
    '.bmp': photoFileIcon,
    '.csv': csvFileIcon,
    '.dib': photoFileIcon,
    '.dgn': vectorFileIcon,
    '.doc': wordFileIcon,
    '.docm': wordFileIcon,
    '.docx': wordFileIcon,
    '.dot': wordTemplateFileIcon,
    '.dotm': wordFileIcon,
    '.dotx': wordTemplateFileIcon,
    '.email': msgFileIcon,
    '.emf': vectorFileIcon,
    '.eml': msgFileIcon,
    '.eps': vectorFileIcon,
    '.exe': exeFileIcon,
    '.flac': audioFileIcon,
    '.flv': videoFileIcon,
    '.gdraw': vectorFileIcon,
    '.gif': photoFileIcon,
    '.htm': codeFileIcon,
    '.html': codeFileIcon,
    '.ical': calendarFileIcon,
    '.icalendar': calendarFileIcon,
    '.ico': photoFileIcon,
    '.ics': calendarFileIcon,
    '.indd': vectorFileIcon,
    '.indt': vectorFileIcon,
    '.jfif': photoFileIcon,
    '.jpe': photoFileIcon,
    '.jpeg': photoFileIcon,
    '.jpg': photoFileIcon,
    '.log': txtFileIcon,
    '.lync': genericFileIcon,
    '.m1v': videoFileIcon,
    '.m4a': audioFileIcon,
    '.mdb': accessFileIcon,
    '.mht': codeFileIcon,
    '.mov': videoFileIcon,
    '.mp2': audioFileIcon,
    '.mp3': audioFileIcon,
    '.mp4': videoFileIcon,
    '.mpa': audioFileIcon,
    '.mpe': videoFileIcon,
    '.mpeg': videoFileIcon,
    '.mpg': videoFileIcon,
    '.mpp': projectFileIcon,
    '.mpt': projectTemplateFileIcon,
    '.mpv2': videoFileIcon,
    '.msg': msgFileIcon,
    '.odp': powerpointFileIcon,
    '.ods': excelFileIcon,
    '.odt': wordFileIcon,
    '.ogg': audioFileIcon,
    '.one': oneFileIcon,
    '.onepkg': onepkgFileIcon,
    '.onetoc': onepkgFileIcon,
    '.onetoc2': onepkgFileIcon,
    '.oxps': vectorFileIcon,
    '.pd': vectorFileIcon,
    '.pdf': pdfFileIcon,
    '.plt': vectorFileIcon,
    '.png': photoFileIcon,
    '.pot': powerpointFileIcon,
    '.potm': powerpointFileIcon,
    '.potx': powerpointTemplateFileIcon,
    '.ppa': powerpointFileIcon,
    '.ppam': powerpointFileIcon,
    '.pps': powerpointFileIcon,
    '.ppsm': powerpointFileIcon,
    '.ppsx': powerpointSlideShowFileIcon,
    '.ppt': powerpointFileIcon,
    '.pptm': powerpointFileIcon,
    '.pptx': powerpointFileIcon,
    '.pro': vectorFileIcon,
    '.ps': vectorFileIcon,
    '.psd': photoFileIcon,
    '.pub': pblshFileIcon,
    '.rpmsg': rpmsgFileIcon,
    '.rtf': txtFileIcon,
    '.sketch': vectorFileIcon,
    '.srt': videoFileIcon,
    '.svg': vectorFileIcon,
    '.svgz': vectorFileIcon,
    '.swf': videoFileIcon,
    '.tif': photoFileIcon,
    '.tiff': photoFileIcon,
    '.txt': txtFileIcon,
    '.url': linkFileIcon,
    '.vcs': calendarFileIcon,
    '.vdx': visioDrawingFileIcon,
    '.vob': videoFileIcon,
    '.vsd': visioDrawingFileIcon,
    '.vsdx': visioDrawingFileIcon,
    '.vsl': visioDrawingFileIcon,
    '.vss': visioDrawingFileIcon,
    '.vssx': visioStencilFileIcon,
    '.vst': visioTemplateFileIcon,
    '.vstx': visioTemplateFileIcon,
    '.wav': audioFileIcon,
    '.wm': audioFileIcon,
    '.wma': audioFileIcon,
    '.wmd': videoFileIcon,
    '.wmf': vectorFileIcon,
    '.wms': videoFileIcon,
    '.wmv': videoFileIcon,
    '.wmz': videoFileIcon,
    '.wps': wordFileIcon,
    '.wri': wordFileIcon,
    '.xcl': excelFileIcon,
    '.xd': vectorFileIcon,
    '.xla': excelFileIcon,
    '.xlam': excelFileIcon,
    '.xls': excelFileIcon,
    '.xlsb': excelFileIcon,
    '.xlsm': excelFileIcon,
    '.xlsx': excelFileIcon,
    '.xlt': excelFileIcon,
    '.xltm': excelFileIcon,
    '.xltx': excelTemplateFileIcon,
    '.xml': codeFileIcon,
    '.xps': vectorFileIcon,
    '.xsn': infopathFileIcon,
    '.zip': zipFileIcon,
});

const wellKnownItemIconMimeTypeMap: {
    [key: string]: FileIcon;
} = Object.freeze({
    'application/vnd.google-apps.document': gdocFileIcon,
    'application/vnd.google-apps.spreadsheet': gsheetFileIcon,
    'application/vnd.google-apps.presentation': gslideFileIcon,
    'application/vnd.google-apps.form': gformFileIcon,
    'application/onenote': oneFileIcon,
    'application/pdf': pdfFileIcon,
});

/* Get icon for file */
export default function getIconForFile(
    fileName: string,
    isFolder: boolean = false,
    mimeType?: string
): FileIcon {
    if (isFolder) {
        return folderIcon;
    }

    let icon = null;
    const fileExtension = getExtensionFromFileName(fileName);
    if (fileExtension) {
        icon = wellKnownFileIconMap[fileExtension.toLowerCase()];
    }

    if (!icon && mimeType && mimeType in wellKnownItemIconMimeTypeMap) {
        return wellKnownItemIconMimeTypeMap[mimeType];
    }

    // If the extension exists in the well known types then return that icon else a generic icon
    return icon || genericFileIcon;
}
